import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { NotificationService } from '@app/core/services/notification.service'
import { AhuState, BaseAhu, Entity } from '@app/shared/models/common-models'
import type { EntityUpdateRequest } from '@app/shared/models/entity-update.model'
import type { FormState } from '@app/shared/models/form.model'
import { SharedAhuService } from '@app/shared/services/ahu/shared-ahu.service'
import { EntityService } from '@app/shared/services/entity.service'
import { EventsService } from '@app/shared/services/events/events.service'
import { UserService } from '@app/user/state/user.service'
import { applyTransaction } from '@datorama/akita'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Observable, of, throwError } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { saveAs } from 'file-saver'
import type { ProjectSearch, ProjectSearchResponse, ProjectUsersResponse } from './project.http.model'
import type {
  AhuResponse,
  BaseProject,
  CreateOrCopyProject,
  Filter,
  Project,
  ProjectAccessRight,
  ProjectDetails,
  ProjectUpdate,
} from './project.model'
import { ProjectsQuery } from './projects.query'
import { ProjectsStore } from './projects.store'

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class ProjectsService {
  constructor(
    protected projectsStore: ProjectsStore,
    private projectsQuery: ProjectsQuery,
    private http: HttpClient,
    private notificationService: NotificationService,
    private entityService: EntityService,
    private sharedAhuService: SharedAhuService,
    private userService: UserService,
    private eventsService: EventsService,
  ) {
    this.userService.settingsChanged$
      .pipe(
        switchMap(() => {
          return applyTransaction(() => {
            const active = this.projectsQuery.getActive()

            this.projectsStore.update(null, entity => {
              return { ...entity, projectDetails: null }
            })

            if (active) {
              return this.resetProject(active.Id, true)
            }
            return of(true)
          })
        }),
        untilDestroyed(this),
      )
      .subscribe()

    this.eventsService.ahuChangedEvent$
      .pipe(
        tap({
          next: ahuId => {
            this.projectsStore.update((project: Project) => project.projectDetails?.Ahus.some(a => a.AhuId === ahuId), {
              projectDetails: null,
            })
          },
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  setActive(id: string | null): void {
    this.projectsStore.setActive(id)
  }

  getProjectUsers(): Observable<Filter> {
    return this.http.get<ProjectUsersResponse>('./Api/project/Users/').pipe(
      map(res => {
        return {
          selectedUser: res.SelectedUser.Id,
          availableUsers: res.Users,
        } as Filter
      }),
      tap(filter => {
        this.projectsStore.update(state => ({ ui: { ...state.ui, filter } }))
      }),
    )
  }

  getProjects(data: ProjectSearch): Observable<ProjectSearchResponse> {
    return this.http.post<ProjectSearchResponse>('./Api/project/search/', data).pipe(
      tap(res => {
        applyTransaction(() => {
          if (data.Items === 0) {
            this.projectsStore.set(res.Projects)
          } else {
            this.projectsStore.upsertMany(res.Projects)
          }
          this.projectsStore.update(state => ({ ui: { ...state.ui, hasMoreItems: res.HasMoreItems } }))
        })
      }),
    )
  }

  getProject(id: string): Observable<ProjectDetails> {
    return this.http.get<ProjectDetails>(`./Api/project/${id}`).pipe(
      tap(projectDetails => {
        this.projectsStore.upsert(id, { projectDetails })
      }),
    )
  }

  updateFilter(selectedUser: number): void {
    applyTransaction(() => {
      this.projectsStore.update(state => ({
        ui: {
          ...state.ui,
          filter: { ...state.ui.filter, selectedUser },
          skipProjects: 0,
        },
      }))
      this.projectsStore.setHasCache(false)
    })
  }

  updateSearchQuery(searchQuery: string): void {
    applyTransaction(() => {
      this.projectsStore.update(state => ({ ui: { ...state.ui, searchQuery, skipProjects: 0 } }))
      this.projectsStore.setHasCache(false)
    })
  }

  loadMoreProjects(): void {
    applyTransaction(() => {
      this.projectsStore.update(state => ({ ui: { ...state.ui, skipProjects: this.projectsQuery.getCount() } }))
      this.projectsStore.setHasCache(false)
    })
  }

  copyProject(id: string): Observable<CreateOrCopyProject> {
    return this.http.post<CreateOrCopyProject>('./Api/project/copy', { ProjectId: id }).pipe(
      tap({
        next: () => {
          this.notificationService.success('UI.Success.CopyProject')
          applyTransaction(() => {
            this.projectsStore.update(state => ({ ui: { ...state.ui, skipProjects: 0 } }))
            this.projectsStore.setHasCache(false)
          })
        },
      }),
      catchError(error => {
        this.notificationService.error('UI.Error.CopyProject')
        throw error
      }),
    )
  }

  createProject(): Observable<CreateOrCopyProject> {
    return this.http.post<CreateOrCopyProject>('./Api/project', {}).pipe(
      tap(() => {
        this.notificationService.success('UI.Success.CreateProject')
        applyTransaction(() => {
          this.projectsStore.update(state => ({ ui: { ...state.ui, skipProjects: 0 } }))
          this.projectsStore.setHasCache(false)
        })
      }),
      catchError(error => {
        this.notificationService.error('UI.Error.CreateProject')
        throw error
      }),
    )
  }

  deleteProject(id: string): Observable<HttpResponse<null>> {
    return this.http.delete<HttpResponse<null>>(`./Api/project/${id}`).pipe(
      tap(() => {
        this.notificationService.success('UI.Success.DeleteProject')
        this.projectsStore.remove(id)
      }),
      catchError(error => {
        this.notificationService.error('UI.Error.DeleteProject')
        throw error
      }),
    )
  }

  shareProject(id: string, shareGroup: ProjectAccessRight): Observable<HttpResponse<null>> {
    return this.http.post<HttpResponse<null>>(`./Api/project/${id}/share`, shareGroup).pipe(
      tap(() => {
        this.notificationService.success(shareGroup.IsSelected ? 'UI.Success.ProjectShare' : 'UI.Success.ProjectUnShare')
      }),
      catchError(error => {
        this.notificationService.error(shareGroup.IsSelected ? 'UI.Error.ProjectShare' : 'UI.Error.ProjectUnShare')
        return throwError(error)
      }),
    )
  }

  updateProject(id: string, properties: EntityUpdateRequest): Observable<ProjectUpdate> {
    return this.http.patch<ProjectUpdate>(`./Api/project/${id}`, properties).pipe(
      tap(projectUpdateResult => {
        applyTransaction(() => {
          this.projectsStore.update(id, oldState => {
            const newState: Project = {
              ...oldState,
              ...this.getUpdatedBaseProjectData(projectUpdateResult.Entity),
              projectDetails: {
                ...oldState.projectDetails,
                Name: this.getPropertyValue(projectUpdateResult.Entity, 'ProjectData', 'Name'),
                Project: projectUpdateResult.Entity,
              },
            }

            // Mark the AHUs in this project as "Not Calculated" because project settings effect calculation results
            newState.projectDetails.Ahus = oldState.projectDetails.Ahus.map(ahu => ({ ...ahu, State: AhuState.NotCalculated }))

            return newState
          })
          this.updateProjectFormState({ isModified: true })
        })
      }),
      catchError(error => {
        this.notificationService.error('UI.Error.ProjectUpdated')
        return throwError(error)
      }),
    )
  }

  resetProject(id: string, omitSuccessMessage: boolean): Observable<boolean> {
    return this.http.post<HttpResponse<null>>(`./Api/project/${id}/reset`, null).pipe(
      switchMap(() => {
        return this.getProject(id)
      }),
      tap(() => {
        this.updateProjectFormState({ isValid: true, isModified: false })
        if (!omitSuccessMessage) {
          this.notificationService.success('UI.Success.DataDiscard')
        }
      }),
      map(() => true),
      catchError(error => {
        this.notificationService.error('UI.Error.DataDiscard')
        return throwError(error)
      }),
    )
  }

  saveProject(id: string, omitSuccessMessage: boolean): Observable<boolean> {
    return this.http.post<HttpResponse<null>>(`./Api/project/${id}`, null).pipe(
      tap(() => {
        this.updateProjectFormState({ isValid: true, isModified: false })
        this.eventsService.projectChanged(id)
        if (!omitSuccessMessage) {
          this.notificationService.success('UI.Success.ProjectSaved')
        }
      }),
      map(() => true),
      catchError(error => {
        this.notificationService.error('UI.Error.ProjectSaved')
        return throwError(error)
      }),
    )
  }

  updateProjectFormState(newState: Partial<FormState>) {
    this.projectsStore.update(state => ({ ui: { ...state.ui, projectFormState: { ...state.ui.projectFormState, ...newState } } }))
  }

  deleteAhu(ahuId: string, id: string): Observable<boolean> {
    return this.sharedAhuService.deleteAhu(ahuId).pipe(
      tap({
        next: () => {
          this.projectsStore.update(id, oldState => {
            return {
              projectDetails: {
                ...oldState.projectDetails,
                Ahus: oldState.projectDetails.Ahus.filter(ahu => ahu.AhuId !== ahuId),
              },
            }
          })
        },
      }),
    )
  }

  exportAhu(id: string): void {
    this.sharedAhuService.downloadXMLExport(id)
  }

  /**
   * Updated a AHU information inside a projects list of ahus
   * @param projectId Project id to update
   * @param ahuId Ahu id to update within the project entity
   * @param valuesToUpdate The values of the ahu to set
   */
  updateProjectAhuCachedProperty(projectId: string, ahuId: string, valuesToUpdate: Partial<BaseAhu>): void {
    this.projectsStore.update(projectId, project => {
      // Ahus are in projectDetails -> if it doesn't exist, we'll stop here
      if (!project.projectDetails) {
        return { ...project }
      }

      // Map the ahus
      const updatedAhus = project.projectDetails.Ahus.map(ahu => {
        if (ahu.AhuId === ahuId) {
          return { ...ahu, ...valuesToUpdate }
        }
        return ahu
      })

      // Return the updated project object
      return {
        ...project,
        projectDetails: {
          ...project.projectDetails,
          Ahus: updatedAhus,
        },
      }
    })
  }

  copyAhu(id: string, ahuId: string): Observable<AhuResponse> {
    return this.http.post<AhuResponse>(`./Api/project/${id}/ahu/${ahuId}/copy`, null).pipe(
      switchMap(response => {
        return this.getProject(id).pipe(map(() => response))
      }),
      tap(() => {
        this.notificationService.success('UI.Success.AhuCopy')
      }),
      catchError(error => {
        this.notificationService.error('UI.Error.AhuCopy')
        return throwError(error)
      }),
    )
  }

  createAhu(id: string, unitType: string, isNotDeprecated: boolean): Observable<AhuResponse> {
    const data = {
      projectId: id,
      ahuType: unitType,
      active: isNotDeprecated,
    }
    return this.http.post<AhuResponse>(`./Api/project/ahu`, data).pipe(
      switchMap(response => {
        return this.getProject(id).pipe(map(() => response))
      }),
      tap(() => {
        this.notificationService.success('UI.Success.CreateAhu')
      }),
      catchError(error => {
        this.notificationService.error('UI.Error.CreateAhu')
        return throwError(error)
      }),
    )
  }

  importAhu(id: string, configFile: File): Observable<boolean> {
    const data = new FormData()
    data.append('file', configFile)

    return this.http.post<AhuResponse>(`./Api/project/${id}/importAhu/xml`, data).pipe(
      switchMap(() => {
        return this.getProject(id)
      }),
      tap(() => {
        this.notificationService.success('UI.Success.ImportAhu')
      }),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.error('UI.Error.ImportAhu')
        return throwError(() => error)
      }),
      map(() => true),
    )
  }

  exportXml = (id: string): void => {
    saveAs(`./Api/project/${id}/export/xml`)
  }

  exportUsageStatistics(): void {
    this.http
      .post('./Api/StatisticsReport/zip', null, { responseType: 'blob', observe: 'response' })
      .pipe(
        tap({
          next: response => {
            const filename = response.headers
              .get('content-disposition')
              .split(';')[1]
              .split('filename')[1]
              .split('=')[1]
              // eslint-disable-next-line quotes
              .split("''")[1]
              .trim()
            saveAs(response.body, decodeURIComponent(filename))
          },
        }),
        catchError(error => {
          this.notificationService.error('UI.Error.ExportStatistics')
          return throwError(error)
        }),
      )
      .subscribe()
  }

  importProject(configFile: File): Observable<CreateOrCopyProject> {
    const data = new FormData()
    data.append('file', configFile)

    return this.http.post<CreateOrCopyProject>(`./Api/project/import/xml`, data).pipe(
      tap(() => {
        this.notificationService.success('UI.Success.ImportProject')
        applyTransaction(() => {
          this.projectsStore.update(state => ({ ui: { ...state.ui, skipProjects: 0 } }))
          this.projectsStore.setHasCache(false)
        })
      }),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.error('UI.Error.ImportProject')
        throw new Error(error.message || error.statusText)
      }),
    )
  }

  private getUpdatedBaseProjectData(project: Entity): Partial<BaseProject> {
    const group = this.entityService.getGroup('ProjectData', project)

    return {
      Name: this.entityService.getProperty('Name', group).Value,
      ProjectId: this.entityService.getProperty('ProjectId', group).Value,
      Customer: this.entityService.getProperty('Customer', group).Value,
      Location: this.entityService.getProperty('Location', group).Value,
      Country: this.entityService.getProperty('Country', group).Value,
      Designer: this.entityService.getProperty('Designer', group).Value,
    } as Partial<BaseProject>
  }

  private getPropertyValue(entity: Entity, groupName: string, propertyName: string): string {
    const group = this.entityService.getGroup(groupName, entity)
    return String(this.entityService.getProperty(propertyName, group).Value ?? '')
  }
}
